import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageSEO from '@/components/feature/PageSEO';

function CategoryCard({ category, index }: { category: typeof categories[0]; index: number }) {
  const bgColors = [
    'bg-accent-100',
    'bg-secondary-100',
    'bg-primary-50',
  ];
  const bgColor = bgColors[index % bgColors.length];
  const isFirst = index === 0;

  return (
    <Link
      to={`/category/${category.slug}`}
      className="group flex flex-col rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
      data-product-shop=""
    >
      <div className={`${bgColor} h-36 flex items-center justify-center`}>
        <div className="w-16 h-16 rounded-2xl bg-background-50/80 flex items-center justify-center">
          <i className={`${category.icon} text-2xl text-foreground-700`} />
        </div>
      </div>
      <div className="p-5 flex flex-col items-center text-center">
        <h4 className="font-semibold text-foreground-900 mb-1 text-sm">{category.name}</h4>
        <p className="text-2xl font-bold text-foreground-800 mb-3">{category.count}<span className="text-xs font-normal text-foreground-400 ml-1">部</span></p>
        <span
          className={`inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
            isFirst
              ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
              : 'border border-foreground-300 text-foreground-700 hover:bg-foreground-50'
          }`}
        >
          浏览分类
        </span>
        <p className="text-[11px] text-foreground-400 mt-3">每日更新热门漫画</p>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const hotComics = comics.filter((c) => c.isHot);
  const featuredCategories = categories.slice(0, 3);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -400 : 400, behavior: 'smooth' });
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩漫网 - 免费韩漫大全',
    description: '免费韩漫大全汇集了众多好看韩国漫画，所有内容均为无删减版本，采用下拉式在线观看技术',
    url: 'http://stgoldcoast.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'http://stgoldcoast.com/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫网上所有漫画内容均为免费阅读。我们致力于为广大漫画爱好者提供优质的免费韩漫大全。',
        },
      },
      {
        '@type': 'Question',
        name: '什么是下拉式在线观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '下拉式在线观看是一种专为手机和电脑优化的漫画阅读方式。您只需向下滑动页面，漫画内容就会自然流畅地展示。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网的漫画都是无删减版本吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫网上的所有漫画内容均为无删减版本，提供完整的原作内容。',
        },
      },
    ],
  };

  return (
    <>
      <PageSEO
        title="韩漫网_免费韩漫大全_好看的韩国漫画_无删减漫画下拉式在线观看"
        description="免费韩漫大全汇集了众多好看韩国漫画，所有内容均为无删减版本，采用下拉式在线观看技术，让您无需等待加载即可流畅阅读，随时随地享受漫画乐趣。"
        keywords="韩漫网,免费韩漫大全,韩国漫画,无删减漫画,下拉式在线观看"
        canonicalUrl="/"
        schemaJson={{ ...pageSchema, ...faqSchema }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex flex-col items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20wide%20artistic%20Korean%20manhwa%20style%20illustration%20featuring%20multiple%20comic%20characters%20in%20dynamic%20poses%2C%20warm%20golden%20and%20coral%20color%20palette%2C%20abstract%20geometric%20shapes%20overlay%2C%20clean%20modern%20composition%2C%20soft%20gradient%20background%2C%20cinematic%20lighting%2C%20vibrant%20but%20harmonious%20colors%2C%20digital%20art%20style&width=1600&height=900&seq=home-hero-bg&orientation=landscape"
            alt="韩漫网 - 免费韩漫大全"
            title="韩漫网 - 好看的韩国漫画在线阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45" />

          <div className="relative z-10 w-full flex flex-col items-center text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-3 tracking-wide">
              免费韩漫大全
            </h1>
            <p className="font-heading text-2xl md:text-4xl font-semibold text-background-50/90 mb-8">
              下拉式无删减阅读
            </p>
            <p className="text-base md:text-lg text-background-50/70 max-w-lg leading-relaxed mb-10">
              汇集众多好看韩国漫画，下拉式流畅阅读体验，无删减完整版本，随时随地享受漫画乐趣
            </p>
            <Link
              to="/category/rexue"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              立即开始阅读
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </section>

        {/* Hot Comics Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  热门<span className="text-primary-500">·连载</span>
                </h2>
                <p className="text-sm text-foreground-500 mt-2">全网最热韩国漫画，每日更新不停歇</p>
              </div>
              <span className="hidden sm:inline-flex items-center px-3 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                HOT
              </span>
            </div>

            <div className="relative">
              {canScrollLeft && (
                <button
                  onClick={() => scroll('left')}
                  className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50 border border-background-200 shadow-sm flex items-center justify-center hover:bg-background-100 transition-all cursor-pointer"
                  aria-label="向左滚动"
                >
                  <i className="ri-arrow-left-s-line text-foreground-700 text-lg w-5 h-5 flex items-center justify-center" />
                </button>
              )}
              <div
                ref={scrollRef}
                onScroll={checkScroll}
                className="flex gap-4 md:gap-5 overflow-x-auto scrollbar-hide pb-2"
                style={{ scrollSnapType: 'x mandatory' }}
              >
                {hotComics.map((comic) => (
                  <div key={comic.id} style={{ scrollSnapAlign: 'start' }}>
                    <ComicCard
                      id={comic.id}
                      slug={comic.slug}
                      title={comic.title}
                      author={comic.author}
                      category={comic.category}
                      rating={comic.rating}
                      cover={comic.cover}
                      chapters={comic.chapters}
                      views={comic.views}
                      isHot
                    />
                  </div>
                ))}
              </div>
              {canScrollRight && (
                <button
                  onClick={() => scroll('right')}
                  className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50 border border-background-200 shadow-sm flex items-center justify-center hover:bg-background-100 transition-all cursor-pointer"
                  aria-label="向右滚动"
                >
                  <i className="ri-arrow-right-s-line text-foreground-700 text-lg w-5 h-5 flex items-center justify-center" />
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Featured Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-light text-foreground-900">
                精选分区
              </h2>
              <p className="text-sm text-foreground-500 mt-2 mb-10">
                按类型浏览，找到你最爱的韩国漫画
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {featuredCategories.map((cat, i) => (
                <CategoryCard key={cat.slug} category={cat} index={i} />
              ))}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/category/rexue"
                className="inline-flex items-center gap-2 text-sm text-primary-500 font-medium hover:text-primary-600 transition-colors cursor-pointer"
              >
                查看全部分类
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </section>

        {/* All Comics Grid */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8">
              全部<span className="text-primary-500">漫画</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {comics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  slug={comic.slug}
                  title={comic.title}
                  author={comic.author}
                  category={comic.category}
                  rating={comic.rating}
                  cover={comic.cover}
                  chapters={comic.chapters}
                  views={comic.views}
                  isHot={comic.isHot}
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}